import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { listChatTemplates, createChatFromTemplate } from '@/services/api/chatbot';
import type { ChatTemplate } from '@/services/api/chatbot';
import { Sparkles, MessageSquare, ChevronRight } from 'lucide-react';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    subdomainId: number;
    onCreated?: (chatId: number) => void;
}

export default function ChatTemplateModal({ open, onOpenChange, subdomainId, onCreated }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState<ChatTemplate | null>(null);

    const { data: templates, isLoading } = useQuery({
        queryKey: ['chatbot-templates'],
        queryFn: listChatTemplates,
        enabled: open,
    });

    const createMut = useMutation({
        mutationFn: (tpl: ChatTemplate) => createChatFromTemplate(subdomainId, tpl.key),
        onSuccess: (chat) => {
            toast({ title: 'Chat criado a partir do template!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-chats', subdomainId] });
            onCreated?.(chat.id);
            onOpenChange(false);
            setSelected(null);
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao criar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-emerald-600" />
                        Criar chat a partir de template
                    </DialogTitle>
                    <DialogDescription>
                        Escolha um template pre-pronto. Cria o chat com <strong>3 quizzes em paralelo</strong> (personas
                        diferentes competindo por peso), regras de ad, cascata de reward e o Topo aparecendo apenas apos
                        o reward (estilo wivlox).
                    </DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <div className="text-center py-12 text-slate-500">Carregando templates...</div>
                ) : (
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                        {(templates || []).map((tpl) => {
                            const isSelected = selected?.key === tpl.key;
                            return (
                                <button key={tpl.key}
                                    onClick={() => setSelected(tpl)}
                                    className={`w-full text-left border rounded-lg p-4 transition ${
                                        isSelected
                                            ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                                            : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                                    }`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`shrink-0 w-10 h-10 rounded-md flex items-center justify-center ${
                                            isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                                        }`}>
                                            <MessageSquare className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-semibold text-sm">{tpl.name}</span>
                                                <span className="text-xs text-slate-500 shrink-0">
                                                    {tpl.quizzes_count} quizzes · {tpl.questions_count} perguntas
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1">{tpl.description}</div>
                                            <div className="text-[10px] text-slate-400 mt-2 font-mono">/{tpl.key}</div>
                                        </div>
                                        {isSelected && <ChevronRight className="w-4 h-4 text-emerald-600 shrink-0 self-center" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={createMut.isPending}>
                        Cancelar
                    </Button>
                    <Button onClick={() => selected && createMut.mutate(selected)}
                        disabled={!selected || createMut.isPending}>
                        {createMut.isPending ? 'Criando...' : 'Criar chat'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
